import { Option } from 'types/Option';

import { BLANK_OPTION_ENABLED } from 'constants/blank';
import {
  AudienceEnum,
  CulturalEnum,
  FormalityEnum,
  GeneratedContentFormatEnum,
  GeneratedContentStyleGuideInput,
  GeneratedContentTypeEnum,
  PurposeEnum,
  StyleEnum,
  ToneEnum
} from 'gql/graphql';
import { decamelize } from 'humps';
import { useLocale, useTranslations } from 'next-intl';
import { useContentGenerationInfo } from 'providers/ContentGenerationProviders/useContentGenerationInfo';
import { useContextLibraryDataAll } from 'providers/ContextLibraryProviders/useContextLibraryDataAll';
import { useContextLibraryTags } from 'providers/ContextLibraryProviders/useContextLibraryTags';
import { useMemo } from 'react';
import { availableLanguageOptions } from 'utils/availableLanguageOptions';
import { getMostUsedTargetLanguagesUtil } from 'utils/getMostUsedTargetLanguagesUtil';

export type StyleGuidesContentGenerationForm = GeneratedContentStyleGuideInput;

export type NamesStyleGuidesContentGenerationCard = keyof StyleGuidesContentGenerationForm;

export const DEFAULT_STYLE_GUIDE: StyleGuidesContentGenerationForm = {
  culturalConsideration: undefined,
  formality: undefined,
  purposeOfText: undefined,
  style: undefined,
  targetAudience: undefined,
  toneOfVoice: undefined
};

export const NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM = Object.keys(DEFAULT_STYLE_GUIDE).reduce(
  (acc, key) => ({ ...acc, [key]: key }),
  {} as Record<NamesStyleGuidesContentGenerationCard, NamesStyleGuidesContentGenerationCard>
);

interface IUseGenerationContentIn {
  contentType: GeneratedContentTypeEnum;
  selectedTagId: string;
  contextIds: string[];
}
interface IUseGenerationContentOut {
  optionsContexts: Option[];
  optionsContentType: Option[];
  optionsContentFormat: Option[];
  optionsLanguages: Option[];
  optionsFilterTags: Option[];
  optionsStyleGuidesCardFields: {
    label: string;
    placeholder: string;
    name: keyof StyleGuidesContentGenerationForm;
    options: Option[];
  }[];
}

export default function useGenerationContent({
  contentType,
  selectedTagId,
  contextIds
}: IUseGenerationContentIn): IUseGenerationContentOut {
  const t = useTranslations();
  const locale = useLocale();

  const { data: info } = useContentGenerationInfo();
  const { data: contexts } = useContextLibraryDataAll();
  const { data: dataTags } = useContextLibraryTags();

  const optionsLanguages = availableLanguageOptions(
    info?.targetLanguageCodes,
    ['tq'],
    getMostUsedTargetLanguagesUtil(info?.languagePairs ?? [], true),
    undefined,
    t,
    locale
  );

  const optionsContexts = useMemo(
    () =>
      contexts?.nodes?.reduce(
        (acc, { filename, id, processed, tags }) =>
          !selectedTagId ||
          (selectedTagId && tags?.some(({ id }) => id === selectedTagId) && processed)
            ? [...acc, { label: filename, value: id } as Option]
            : acc,
        [] as Option[]
      ) ?? ([] as Option[]),
    [contexts, selectedTagId]
  );

  const optionsFilterTags = useMemo(
    () => [
      {
        label: t('placeholders.search_tags'),
        value: ''
      },
      ...(dataTags?.map((tags) => ({
        label: tags.name,
        value: tags.id
      })) ?? [])
    ],
    [t, dataTags]
  );

  const optionsContentType = [
    BLANK_OPTION_ENABLED,
    ...[
      GeneratedContentTypeEnum.WrittenContent,
      GeneratedContentTypeEnum.AudioContent,
      GeneratedContentTypeEnum.InteractiveContent,
      GeneratedContentTypeEnum.Presentations
      // GeneratedContentTypeEnum.ContentRepurposing
    ].map((item) => ({
      value: item,
      label: t(('content_generation.content_types.' + item) as any)
    }))
  ];

  const optionsContentFormatBulk = {
    [GeneratedContentTypeEnum.AudioContent]: [
      GeneratedContentFormatEnum.ScriptsForPodcasts,
      GeneratedContentFormatEnum.ScriptsForVoiceovers,
      GeneratedContentFormatEnum.ScriptsForExplainerVideos,
      GeneratedContentFormatEnum.ScriptsForTutorials,
      GeneratedContentFormatEnum.ScriptsForWebinars
    ],
    [GeneratedContentTypeEnum.ContentRepurposing]: [
      GeneratedContentFormatEnum.TransformingBlogPosts,
      GeneratedContentFormatEnum.SummarizingArticles,
      GeneratedContentFormatEnum.ConvertingWebinars
    ],
    [GeneratedContentTypeEnum.InteractiveContent]: [
      GeneratedContentFormatEnum.QuestionsForSurveys,
      GeneratedContentFormatEnum.IdeasForInfographics,
      GeneratedContentFormatEnum.ConceptsForWeb,
      GeneratedContentFormatEnum.ScriptsForVideos
    ],
    [GeneratedContentTypeEnum.Presentations]: [
      GeneratedContentFormatEnum.TextForSalesDecks,
      GeneratedContentFormatEnum.TextForPitchDecks,
      GeneratedContentFormatEnum.TextForSlidePresentations
    ],
    [GeneratedContentTypeEnum.WrittenContent]: [
      GeneratedContentFormatEnum.BlogPosts,
      GeneratedContentFormatEnum.Articles,
      GeneratedContentFormatEnum.WebsiteCopy,
      GeneratedContentFormatEnum.SocialMediaPosts,
      GeneratedContentFormatEnum.Newsletters
    ]
  };

  const optionsContentFormat = [
    BLANK_OPTION_ENABLED,
    ...[...(contentType ? optionsContentFormatBulk[contentType] : [])].map((item) => ({
      value: item,
      label: t(('content_generation.content_formats.' + item) as any)
    }))
  ];

  const optionsPurpose = [
    BLANK_OPTION_ENABLED,
    {
      value: PurposeEnum.Marketing,
      label: t('style_guides.index.items.purpose.marketing')
    },
    {
      value: PurposeEnum.TechnicalDocumentation,
      label: t('style_guides.index.items.purpose.technical_documentation')
    },
    {
      value: PurposeEnum.Internal,
      label: t('style_guides.index.items.purpose.internal_communication')
    },
    {
      value: PurposeEnum.Legal,
      label: t('style_guides.index.items.purpose.legal')
    },
    {
      value: PurposeEnum.Educational,
      label: t('style_guides.index.items.purpose.educational')
    },
    {
      value: PurposeEnum.Branding,
      label: t('style_guides.index.items.purpose.branding')
    },
    {
      value: PurposeEnum.ProductDescriptions,
      label: t('style_guides.index.items.purpose.product_descriptions')
    },
    {
      value: PurposeEnum.Website,
      label: t('style_guides.index.items.purpose.static_website_shop_content')
    },
    {
      value: PurposeEnum.Seo,
      label: t('style_guides.index.items.purpose.seo_landing_page')
    }
  ];

  const optionsTarget = [
    BLANK_OPTION_ENABLED,
    {
      value: AudienceEnum.Customers,
      label: t('style_guides.index.items.target_audience.consumers')
    },
    {
      value: AudienceEnum.Employees,
      label: t('style_guides.index.items.target_audience.employees')
    },
    {
      value: AudienceEnum.Stakeholders,
      label: t('style_guides.index.items.target_audience.stakeholders')
    },
    {
      value: AudienceEnum.Suppliers,
      label: t('style_guides.index.items.target_audience.suppliers')
    },
    {
      value: AudienceEnum.Investors,
      label: t('style_guides.index.items.target_audience.investors')
    },
    {
      value: AudienceEnum.Public,
      label: t('style_guides.index.items.target_audience.general_public')
    },
    {
      value: AudienceEnum.Technical,
      label: t('style_guides.index.items.target_audience.technical_professionals')
    }
  ];

  const optionsFormality = [
    BLANK_OPTION_ENABLED,
    {
      value: FormalityEnum.Formal,
      label: t('style_guides.index.items.formality.formal')
    },
    {
      value: FormalityEnum.Informal,
      label: t('style_guides.index.items.formality.informal')
    }
  ];

  const optionsStyle = [
    BLANK_OPTION_ENABLED,
    {
      value: StyleEnum.Formal,
      label: t('style_guides.index.items.style.formal')
    },
    {
      value: StyleEnum.Casual,
      label: t('style_guides.index.items.style.casual')
    },
    {
      value: StyleEnum.Professional,
      label: t('style_guides.index.items.style.professional')
    },
    {
      value: StyleEnum.Corporate,
      label: t('style_guides.index.items.style.corporate')
    },
    {
      value: StyleEnum.Playful,
      label: t('style_guides.index.items.style.playful')
    },
    {
      value: StyleEnum.Scentific,
      label: t('style_guides.index.items.style.scientific')
    }
  ];

  const optionsToneOfVoice = [
    BLANK_OPTION_ENABLED,
    {
      value: ToneEnum.Friendly,
      label: t('style_guides.index.items.voice_tone.friendly')
    },
    {
      value: ToneEnum.Authoritative,
      label: t('style_guides.index.items.voice_tone.authoritative')
    },
    {
      value: ToneEnum.Informative,
      label: t('style_guides.index.items.voice_tone.informative')
    },
    {
      value: ToneEnum.Serious,
      label: t('style_guides.index.items.voice_tone.serious')
    },
    {
      value: ToneEnum.Humorous,
      label: t('style_guides.index.items.voice_tone.humorous')
    }
  ];

  const optionsCultural = [
    BLANK_OPTION_ENABLED,
    {
      value: CulturalEnum.MaleFemale,
      label: t('style_guides.index.items.cultural_considerations.male_female')
    },
    {
      value: CulturalEnum.NonBinary,
      label: t('style_guides.index.items.cultural_considerations.neutral_non_binary')
    },
    {
      value: CulturalEnum.GenderNeutral,
      label: t(
        'style_guides.index.items.cultural_considerations.gender_inclusive_gender_neutral_language'
      )
    }
  ];

  const optionsStyleGuidesCardFields = [
    {
      options: optionsPurpose,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.purposeOfText
    },
    {
      options: optionsTarget,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.targetAudience
    },
    {
      options: optionsFormality,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.formality
    },
    {
      options: optionsStyle,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.style
    },
    {
      options: optionsToneOfVoice,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.toneOfVoice
    },
    {
      options: optionsCultural,
      name: NAMES_STYLE_GUIDES_CONTENT_CREATOR_FORM.culturalConsideration
    }
  ]?.map(({ name, options }) => ({
    ...{ name, options },
    label: t(`labels.${decamelize(name)}` as any),
    placeholder: t(`placeholders.${decamelize(name)}` as any)
  }));

  return {
    optionsStyleGuidesCardFields,
    optionsLanguages,
    optionsContexts,
    optionsContentType,
    optionsContentFormat,
    optionsFilterTags
  };
}
